import type { InitialIngredientQuantities, ModificationsMap } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates raw portion options for an ingredient in a paired included/extras group.
 */
export function getPortionOptions(params: Params): PortionOption[] | undefined {
  const {
    ingredientId,
    currentPortions,
    groupActiveCount,
    maxPortions,
    freePortions,
    allModificationsMap,
    extrasModificationsMap,
    initialIngredientQuantities,
  } = params;

  if (!ingredientId || !allModificationsMap || maxPortions <= 1) return;

  const modification = allModificationsMap.get(ingredientId);
  const extrasModification = modification?.ingredientId
    ? extrasModificationsMap.get(modification.ingredientId)
    : undefined;

  const initialQuantity = initialIngredientQuantities.get(ingredientId) ?? 0;
  const includedPrice = modification?.additionCost ?? 0;
  const extrasPrice = extrasModification?.additionCost ?? 0;
  const otherIngredientsCount = groupActiveCount - currentPortions;
  const availableFreeSlots = Math.max(0, freePortions - otherIngredientsCount);
  const availablePortions = maxPortions - otherIngredientsCount;
  const visiblePortions = Math.min(availablePortions, MAX_VISIBLE_PORTION_OPTIONS);

  // Three-tier cost: initial portions are free (already in the product price),
  // remaining included-group slots cost includedPrice, extras slots cost extrasPrice.
  return Array.from({ length: visiblePortions }, (_, index) => {
    const portions = index + 1;
    const freeFromInitial = Math.min(portions, initialQuantity, availableFreeSlots);
    const paidIncludedPortions = Math.max(
      0,
      Math.min(portions, availableFreeSlots) - freeFromInitial,
    );
    const extraPortions = Math.max(0, portions - availableFreeSlots);

    return {
      portions,
      extrasCost: paidIncludedPortions * includedPrice + extraPortions * extrasPrice,
    };
  });
}

// ─── Constants ──────────────────────────────────────────────────────────────

const MAX_VISIBLE_PORTION_OPTIONS = 3;

// ─── Types ──────────────────────────────────────────────────────────────────

export type PortionOption = {
  portions: number;
  extrasCost: number;
};

type Params = {
  ingredientId: string | undefined;
  currentPortions: number;
  groupActiveCount: number;
  maxPortions: number;
  freePortions: number;
  allModificationsMap: ModificationsMap | undefined;
  extrasModificationsMap: ModificationsMap;
  initialIngredientQuantities: InitialIngredientQuantities;
};
